<template>
  <div>
    <!-- ============================================================
       Dialog Thêm mới / Cập nhật khoản thu gốc (tutition_v3)
       Sử dụng: <principal-revenue-form-dialog ref="formDialog" @saved="fetchData" />
       Mở:     this.$refs.formDialog.openForCreate()
               this.$refs.formDialog.openForEdit(id)
       ============================================================ -->
    <dialog-base
      :visible.sync="visible"
      :title="isEditMode ? 'Cập nhật khoản thu' : 'Thêm mới khoản thu'"
      size="lg"
      cancel-text="Hủy"
      confirm-text="Lưu lại"
      :confirm-loading="loading"
      :use-panel="false"
      top="4vh"
      @confirm="handleSubmit"
      @cancel="handleClose"
    >
      <el-form
        ref="dataForm"
        :model="dataForm"
        :rules="formRules"
        label-width="200px"
        label-position="left"
        class="sk-dialog-content"
      >
        <div class="prf-help-entry">
          <el-tooltip content="Xem toàn bộ hướng dẫn thao tác" placement="top">
            <el-button type="primary" icon="el-icon-question" plain size="small" @click="openGuideDialog">
              Hướng dẫn thao tác
            </el-button>
          </el-tooltip>
        </div>
      <!-- ══════════════════════════════════════════════════════
           PHÂN KHU 1 – THÔNG TIN CHUNG
           ══════════════════════════════════════════════════════ -->
      <div class="sk-panel mb-4">
        <div class="sk-panel-head">
          <div class="sk-panel-icon"><i class="el-icon-document" /></div>
          <div>
            <p class="sk-panel-title">Thông tin chung</p>
            <p class="sk-panel-subtitle">Tên, loại và mô tả khoản thu</p>
          </div>
        </div>

        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item prop="name">
              <template v-slot:label>
                Tên khoản thu
                <el-tooltip content="Tên hiển thị của khoản thu (ví dụ: Học phí tháng)" placement="top">
                  <i class="el-icon-question prf-help-icon ml-2" />
                </el-tooltip>
              </template>
              <el-input v-model="dataForm.name" placeholder="Nhập tên khoản thu..." maxlength="255" show-word-limit />
            </el-form-item>
          </el-col>

          <el-col :span="12">
            <el-form-item prop="category">
              <template v-slot:label>
                Loại khoản
                <el-tooltip content="Chọn khoản thu hoặc khoản hoàn trả" placement="top">
                  <i class="el-icon-question prf-help-icon ml-2" />
                </el-tooltip>
              </template>
              <el-select v-model="dataForm.category" placeholder="Chọn loại" class="sk-u-w-full" @change="onCategoryChange">
                <el-option label="Khoản thu" value="IN" />
                <el-option label="Khoản hoàn trả" value="OUT" />
              </el-select>
            </el-form-item>
          </el-col>

          <el-col :span="12">
            <el-form-item prop="unit">
              <template v-slot:label>
                Đơn vị tính
                <el-tooltip content="Chọn đơn vị hoặc nhập mới (lưu tạm vào danh sách)." placement="top">
                  <i class="el-icon-question prf-help-icon ml-2" />
                </el-tooltip>
              </template>
              <el-select
                v-model="dataForm.unit"
                filterable
                allow-create
                default-first-option
                placeholder="Chọn hoặc nhập đơn vị"
                class="sk-u-w-full"
              >
                <el-option v-for="u in unitsList" :key="u" :label="u" :value="u" />
              </el-select>
            </el-form-item>
          </el-col>

          <el-col :span="24">
            <el-form-item>
              <template v-slot:label>
                Mô tả
                <el-tooltip content="Mô tả ngắn về khoản thu, tối đa 255 ký tự" placement="top">
                  <i class="el-icon-question prf-help-icon ml-2" />
                </el-tooltip>
              </template>
              <el-input v-model="dataForm.description" type="textarea" :rows="2" autosize placeholder="Nhập mô tả..." maxlength="255" show-word-limit />
            </el-form-item>
          </el-col>
        </el-row>
      </div>

      <!-- ══════════════════════════════════════════════════════
           PHÂN KHU 2 – TÀI CHÍNH & THÀNH TIỀN
           ══════════════════════════════════════════════════════ -->
      <div class="sk-panel mb-4">
        <div class="sk-panel-head">
          <div class="sk-panel-icon sk-panel-icon--success"><i class="el-icon-money" /></div>
          <div>
            <p class="sk-panel-title">Tài chính</p>
            <p class="sk-panel-subtitle">Đơn giá, số lượng, hóa đơn điện tử và chính sách giảm giá</p>
          </div>
        </div>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item prop="price">
              <template v-slot:label>
                Đơn giá (VNĐ)
                <el-tooltip content="Đơn giá cho 1 đơn vị tinhs (VNĐ)" placement="top">
                  <i class="el-icon-question prf-help-icon ml-2" />
                </el-tooltip>
              </template>
              <ElCurrencyInput
                v-model="dataForm.price"
                :options="{ locale: 'vi-VN', precision: 0, autoDecimalMode: false, distractionFree: false }"
                class="sk-u-w-full"
                @input="calculatePreview"
              />
            </el-form-item>
          </el-col>

          <el-col :span="12">
            <el-form-item prop="quantity">
              <template v-slot:label>
                Số lượng
                <el-tooltip content="Số lượng đơn vị áp dụng cho khoản (ví dụ: 1)" placement="top">
                  <i class="el-icon-question prf-help-icon ml-2" />
                </el-tooltip>
              </template>
              <el-input
                v-model.number="dataForm.quantity"
                type="number"
                :step="allowDecimal ? 0.01 : 1"
                class="sk-u-w-full"
                @input="calculatePreview"
              />
            </el-form-item>
          </el-col>

          <el-col :span="12">
            <el-form-item label="Cho phép số thập phân">
              <el-switch
                :value="allowDecimal"
                active-text="Có"
                inactive-text="Không"
                @change="handleAllowDecimalChange"
              />
            </el-form-item>
          </el-col>

          <!-- Hóa đơn điện tử -->
<!--          <el-col :span="12">-->
<!--            <el-form-item>-->
<!--              <template v-slot:label>-->
<!--                Hóa đơn điện tử-->
<!--                <el-tooltip content="Bật nếu khoản thu này cần phát hành hóa đơn điện tử." placement="top">-->
<!--                  <i class="el-icon-question prf-help-icon ml-2" />-->
<!--                </el-tooltip>-->
<!--              </template>-->
<!--              <el-radio-group v-model="dataForm.issuingElectronicInvoices">-->
<!--                <el-radio :label="true">Có</el-radio>-->
<!--                <el-radio :label="false">Không</el-radio>-->
<!--              </el-radio-group>-->
<!--            </el-form-item>-->
<!--          </el-col>-->

          <!-- Giảm giá -->
          <el-col :span="12">
            <el-form-item prop="discount">
              <template v-slot:label>
                Giảm giá
                <el-tooltip content="Bật nếu muốn áp dụng chính sách giảm giá (theo % hoặc theo số tiền)." placement="top">
                  <i class="el-icon-question prf-help-icon ml-2" />
                </el-tooltip>
              </template>
              <el-radio-group v-model="dataForm.discount" @change="onDiscountChange">
                <el-radio :label="true">Có</el-radio>
                <el-radio :label="false">Không</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>

          <el-col v-if="dataForm.discount" :span="12">
            <el-form-item prop="discountType">
              <template v-slot:label>
                Loại giảm giá
                <el-tooltip content="Chọn loại giảm: phần trăm (%) hoặc số tiền (VNĐ)." placement="top">
                  <i class="el-icon-question prf-help-icon ml-2" />
                </el-tooltip>
              </template>
              <el-select v-model="dataForm.discountType" placeholder="Chọn loại" class="sk-u-w-full" @change="calculatePreview">
                <el-option label="Phần trăm (%)" value="PERCENT" />
                <el-option label="Số tiền (VNĐ)" value="MONEY" />
              </el-select>
            </el-form-item>
          </el-col>

          <el-col v-if="dataForm.discount" :span="12">
            <el-form-item prop="discountNumber">
              <template v-slot:label>
                <span v-if="dataForm.discountType === 'PERCENT'">Phần trăm giảm (%)</span>
                <span v-else>Số tiền giảm (VNĐ)</span>
                <el-tooltip content="Nhập giá trị muốn giảm" placement="top">
                  <i class="el-icon-question prf-help-icon ml-2" />
                </el-tooltip>
              </template>
              <div class="flex items-center">
                <template v-if="dataForm.discountType === 'MONEY'">
                  <ElCurrencyInput
                    v-model="dataForm.discountNumber"
                    :options="{ locale: 'vi-VN', precision: 0, autoDecimalMode: false, distractionFree: false }"
                    class="sk-u-w-full"
                    @input="calculatePreview"
                  />
                </template>
                <template v-else>
                  <el-input-number v-discount-percent
                    v-model="dataForm.discountNumber"
                    :min="0"
                    :max="100"
                    :precision="0"
                    controls-position="right"
                    class="sk-u-w-full"
                    @change="calculatePreview"
                  />
                </template>
                <div v-if="discountAmount !== null && discountAmount > 0" class="prf-discount-amount ml-3">-{{ formatMoney(discountAmount) }}</div>
              </div>
            </el-form-item>
          </el-col>

          <!-- Thành tiền (FE preview – KHÔNG gửi lên BE) -->
          <el-col :span="24">
            <el-form-item>
              <template v-slot:label>
                Thành tiền
                <el-tooltip content="Số tiền cuối cùng sau khi tính toán" placement="top">
                  <i class="el-icon-question prf-help-icon ml-2" />
                </el-tooltip>
              </template>
              <div class="prf-preview-money">{{ formatMoney(previewSumMoney) }}</div>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item v-if="previewSumMoney > 0">
              <template v-slot:label>
                Số tiền bằng chữ
                <el-tooltip content="Số tiền bằng chữ (tiếng Việt) của thành tiền tham khảo." placement="top">
                  <i class="el-icon-question prf-help-icon ml-2" />
                </el-tooltip>
              </template>
              <div class="prf-money-words">{{ numberToWordsVN(previewSumMoney) }}</div>
            </el-form-item>
          </el-col>
        </el-row>
      </div>

      <!-- ══════════════════════════════════════════════════════
           PHÂN KHU 3 – THỜI GIAN ÁP DỤNG
           ══════════════════════════════════════════════════════ -->
      <div class="sk-panel mb-4">
        <div class="sk-panel-head">
          <div class="sk-panel-icon sk-panel-icon--warning"><i class="el-icon-date" /></div>
          <div>
            <p class="sk-panel-title">Thời gian áp dụng</p>
            <p class="sk-panel-subtitle">Cấu hình tháng, khoảng thời gian hoặc số đơn vị áp dụng</p>
          </div>
        </div>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item prop="appliedTimeType">
              <template v-slot:label>
                Kiểu thời gian
                <el-tooltip content="Thời gian mà khoản được áp " placement="top">
                  <i class="el-icon-question prf-help-icon ml-2" />
                </el-tooltip>
              </template>
              <el-select v-model="dataForm.appliedTimeType" placeholder="Chọn kiểu thời gian" class="sk-u-w-full" @change="onAppliedTimeTypeChange">
                <el-option label="Theo tháng" value="MONTH" />
                <el-option label="Theo khoảng ngày" value="TIME_PERIOD" />
              </el-select>
            </el-form-item>
          </el-col>

          <!-- MONTH: chọn tháng áp dụng -->
          <template v-if="dataForm.appliedTimeType === 'MONTH'">
            <el-col :span="24">
              <el-form-item>
                <template v-slot:label>
                  Tháng áp dụng
                  <el-tooltip content="Chọn các tháng trong năm mà khoản thu này được áp dụng." placement="top">
                    <i class="el-icon-question prf-help-icon ml-2" />
                  </el-tooltip>
                </template>
                <div class="prf-month-grid">
                          <div style="width:100%; display:flex; justify-content:flex-end; margin-bottom:6px;">
                            <el-checkbox v-model="monthsAll">Chọn tất cả</el-checkbox>
                          </div>
                  <el-checkbox v-for="m in monthList" :key="m.key" v-model="dataForm[m.key]" :label="m.label" border size="small" class="prf-month-cb" />
                </div>
              </el-form-item>
            </el-col>

            <!-- <el-col :span="24">
              <el-form-item>
                <template v-slot:label>
                  Chu kỳ mặc định
                  <el-tooltip content="Sử dụng để tính toán khi áp dụng điểm danh" placement="top">
                    <i class="el-icon-question prf-help-icon ml-2" />
                  </el-tooltip>
                </template>
                <el-radio-group v-model="dataForm.defaultCycle">
                  <el-radio :label="true">Cả tháng</el-radio>
                  <el-radio :label="false">Tùy chỉnh ngày</el-radio>
                </el-radio-group>
              </el-form-item>
            </el-col>

            <template v-if="dataForm.defaultCycle === false">
              <el-col :span="12">
                <el-form-item prop="startDay">
                  <template v-slot:label>
                    Ngày bắt đầu
                    <el-tooltip content="Ngày bắt đầu tính trong tháng (1–31)." placement="top">
                      <i class="el-icon-question prf-help-icon ml-2" />
                    </el-tooltip>
                  </template>
                  <el-input-number v-model="dataForm.startDay" :min="1" :max="31" :precision="0" controls-position="right" class="sk-u-w-full" />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item prop="endDay">
                  <template v-slot:label>
                    Ngày kết thúc
                    <el-tooltip content="Ngày kết thúc tính trong tháng (1–31)." placement="top">
                      <i class="el-icon-question prf-help-icon ml-2" />
                    </el-tooltip>
                  </template>
                  <el-input-number v-model="dataForm.endDay" :min="1" :max="31" :precision="0" controls-position="right" class="sk-u-w-full" />
                </el-form-item>
              </el-col>
            </template> -->
          </template>


          <!-- TIME_PERIOD: startDate / endDate -->
          <template v-if="dataForm.appliedTimeType === 'TIME_PERIOD'">
            <el-col :span="12">
              <el-form-item prop="startDate" label="Ngày bắt đầu">
                <el-date-picker
                  v-model="dataForm.startDate"
                  type="date"
                  placeholder="Chọn ngày bắt đầu"
                  value-format="yyyy-MM-dd"
                  class="sk-u-w-full"
                />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item prop="endDate" label="Ngày kết thúc">
                <el-date-picker
                  v-model="dataForm.endDate"
                  type="date"
                  placeholder="Chọn ngày kết thúc"
                  value-format="yyyy-MM-dd"
                  class="sk-u-w-full"
                />
              </el-form-item>
            </el-col>
          </template>

          <!-- numberUnit: luôn hiển thị -->
<!--          <el-col :span="12">-->
<!--            <el-form-item prop="numberUnit">-->
<!--              <template v-slot:label>-->
<!--                Số đơn vị-->
<!--                <el-tooltip content="Giới hạn số lượng đơn vị tối đa được áp dụng cho khoản." placement="top">-->
<!--                  <i class="el-icon-question prf-help-icon ml-2" />-->
<!--                </el-tooltip>-->
<!--              </template>-->
<!--              <el-input-number v-model="dataForm.numberUnit" :min="0" :precision="0" controls-position="right" class="sk-u-w-full" />-->
<!--            </el-form-item>-->
<!--          </el-col>-->
        </el-row>
      </div>

      <!-- ══════════════════════════════════════════════════════
           PHÂN KHU 4 – CẤU HÌNH ĐIỂM DANH
           ══════════════════════════════════════════════════════ -->
      <div class="sk-panel">
        <div class="sk-panel-head">
          <div class="sk-panel-icon sk-panel-icon--danger"><i class="el-icon-user" /></div>
          <div>
            <p class="sk-panel-title">Cấu hình điểm danh</p>
            <p class="sk-panel-subtitle">Thiết lập tính phí theo điểm danh thực tế</p>
          </div>
        </div>

        <el-row :gutter="20">
          <!-- Áp dụng điểm danh -->
          <el-col :span="12">
            <el-form-item prop="attendance">
              <template v-slot:label>
                Áp dụng điểm danh
                <el-tooltip content="Bật nếu khoản thu tính dựa trên điểm danh thực tế." placement="top">
                  <i class="el-icon-question prf-help-icon ml-2" />
                </el-tooltip>
              </template>
              <el-radio-group v-model="dataForm.attendance" @change="onAttendanceChange">
                <el-radio :label="true">Có</el-radio>
                <el-radio :label="false">Không</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>

          <!-- Phương pháp tính – chỉ hiển thị với Khoản thu (IN), OUT luôn là AFTER -->
          <el-col v-if="dataForm.attendance" :span="12">
            <el-form-item prop="calculationMethod">
              <template v-slot:label>
                Phương pháp tính
                <el-tooltip content="Tính trước: tính theo lịch học. Tính sau: tính theo điểm danh thực tế." placement="top">
                  <i class="el-icon-question prf-help-icon ml-2" />
                </el-tooltip>
              </template>
              <el-radio-group
                v-model="dataForm.calculationMethod"
                :disabled="dataForm.category === 'OUT'"
                @change="resetAttendanceMethod"
              >
                <el-radio label="BEFORE" :disabled="dataForm.category === 'OUT'">Tính trước</el-radio>
                <el-radio label="AFTER">Tính sau</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>

          <!-- Loại điểm danh – options phụ thuộc category + calculationMethod -->
          <el-col v-if="dataForm.attendance && attendanceTypeCurrentList.length > 0" :span="12">
            <el-form-item prop="attendanceType">
              <template v-slot:label>
                Loại điểm danh
                <el-tooltip content="Chọn loại điểm danh tương ứng với phương pháp tính." placement="top">
                  <i class="el-icon-question prf-help-icon ml-2" />
                </el-tooltip>
              </template>
              <el-select
                v-model="dataForm.attendanceType"
                placeholder="Chọn loại điểm danh"
                class="sk-u-w-full"
                @change="changeAttendanceTypeMethod"
              >
                <el-option
                  v-for="item in attendanceTypeCurrentList"
                  :key="item.key"
                  :value="item.key"
                  :label="item.value"
                />
              </el-select>
            </el-form-item>
          </el-col>

          <!-- Chi tiết (configKey) – hiển thị khi có danh sách chi tiết -->
          <el-col v-if="dataForm.attendance && configKeyCurrentList.length > 0" :span="12">
            <el-form-item prop="configKey">
              <template v-slot:label>
                Chi tiết
                <el-tooltip content="Chọn chi tiết tương ứng với loại điểm danh." placement="top">
                  <i class="el-icon-question prf-help-icon ml-2" />
                </el-tooltip>
              </template>
              <el-select v-model="dataForm.configKey" placeholder="Chọn chi tiết" class="sk-u-w-full">
                <el-option
                  v-for="item in configKeyCurrentList"
                  :key="item.key"
                  :value="item.key"
                  :label="item.value"
                />
              </el-select>
            </el-form-item>
          </el-col>

          <!-- Cấu hình theo block – chỉ hiển thị khi IN + AFTER + LATE -->
          <el-col v-if="dataForm.attendance && dataForm.category === 'IN' && dataForm.calculationMethod === 'AFTER' && dataForm.attendanceType === 'LATE'" :span="12">
            <el-form-item>
              <template v-slot:label>
                Giờ đón về
                <el-tooltip content="Thời gian đón về chuẩn (HH:mm), dùng để xác định khung đón muộn." placement="top">
                  <i class="el-icon-question prf-help-icon ml-2" />
                </el-tooltip>
              </template>
              <el-time-picker
                format="HH:mm"
                v-model="dataForm.configTime"
                :picker-options="{ format: 'HH:mm' }"
                value-format="HH:mm"
                placeholder="Chọn giờ đón về"
                class="sk-u-w-full"
              />
            </el-form-item>
          </el-col>

          <el-col v-if="dataForm.attendance && dataForm.category === 'IN' && dataForm.calculationMethod === 'AFTER' && dataForm.attendanceType === 'LATE'" :span="24">
            <el-form-item>
              <template v-slot:label>
                Cấu hình đón muộn
                <el-tooltip content="Mỗi block tương ứng 1 khoảng thời gian đón muộn và mức thu tiền tương ứng." placement="top">
                  <i class="el-icon-question prf-help-icon ml-2" />
                </el-tooltip>
              </template>

              <!-- Header labels -->
              <el-row :gutter="10" class="prf-config-header mb-1">
                <el-col :span="6"><span class="prf-col-label">Tên block</span></el-col>
                <el-col :span="6"><span class="prf-col-label">Từ giờ</span></el-col>
                <el-col :span="6"><span class="prf-col-label">Đến giờ</span></el-col>
                <el-col :span="5"><span class="prf-col-label">Số tiền (VNĐ)</span></el-col>
                <el-col :span="1" />
              </el-row>

              <div v-if="dataForm.configList.length === 0" class="prf-empty-config">Chưa có cấu hình. Nhấn "+ Thêm" để bắt đầu.</div>

              <div v-for="(cfg, idx) in dataForm.configList" :key="idx" class="prf-config-row mb-2">
                <el-row :gutter="10" align="middle" type="flex">
                  <!-- Tên block -->
                  <el-col :span="6">
                    <el-input v-model="cfg.configName" placeholder="Tên (vd: Muộn 30 phút)" size="small" />
                  </el-col>
                  <!-- Giờ bắt đầu -->
                  <el-col :span="6">
                    <el-time-picker
                      v-model="cfg.configStartTime"
                      format="HH:mm"
                      :picker-options="{ format: 'HH:mm' }"
                      value-format="HH:mm"
                      placeholder="Từ giờ"
                      size="small"
                      class="sk-u-w-full"
                    />
                  </el-col>
                  <!-- Giờ kết thúc -->
                  <el-col :span="6">
                    <el-time-picker
                      v-model="cfg.configEndTime"
                      format="HH:mm"
                      :picker-options="{ format: 'HH:mm' }"
                      value-format="HH:mm"
                      placeholder="Đến giờ"
                      size="small"
                      class="sk-u-w-full"
                    />
                  </el-col>
                  <!-- Số tiền -->
                  <el-col :span="5">
                    <ElCurrencyInput
                      v-model="cfg.configValue"
                      :options="{ locale: 'vi-VN', precision: 0, autoDecimalMode: false, distractionFree: false }"
                      size="small"
                      class="sk-u-w-full"
                    />
                  </el-col>
                  <!-- Xóa -->
                  <el-col :span="1" class="text-right">
                    <el-button type="danger" icon="el-icon-delete" size="mini" circle @click="removeConfig(idx)" />
                  </el-col>
                </el-row>
              </div>

              <el-button type="primary" size="small" icon="el-icon-plus" plain @click="addConfig">Thêm khoảng thời gian</el-button>
            </el-form-item>
          </el-col>
        </el-row>
      </div>

      <!-- ══════════════════════════════════════════════════════
           PHÂN KHU 5 – ÁP DỤNG CHO HS ĐÃ ĐĂNG KÝ (chỉ hiện khi sửa)
           ══════════════════════════════════════════════════════ -->
      <div v-if="isEditMode" class="sk-panel mt-4">
        <div class="sk-panel-head">
          <div class="sk-panel-icon sk-panel-icon--danger"><i class="el-icon-warning-outline" /></div>
          <div>
            <p class="sk-panel-title">Áp dụng cho học sinh đã đăng ký</p>
            <p class="sk-panel-subtitle">Ghi đè giá mới xuống tất cả học sinh đã đăng ký cho khoản phụ này</p>
          </div>
        </div>

        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item label-width="0">
              <el-checkbox v-model="updateRegisteredOverride" class="prf-override-checkbox">
                Áp dụng thay đổi cho HS đã đăng ký
              </el-checkbox>
              <div v-if="updateRegisteredOverride" class="prf-override-warning">
                <i class="el-icon-warning" />
                <span>
                  Lưu ý: Giá trị riêng đã sửa cho từng HS sẽ bị ghi đè bằng giá mới.
                  Hành động này không thể hoàn tác.
                </span>
              </div>
            </el-form-item>
          </el-col>
        </el-row>
      </div>
      </el-form>
    </dialog-base>

    <principal-revenue-guide-dialog ref="guideDialog" />
  </div>
</template>

<script>
import { discountPercentInput, hasOverLimitPercentDiscount, DISCOUNT_PERCENT_LIMIT_MESSAGE } from "@/utils/discountPercent";
import DialogBase from "@/components/DialogBase.vue";
import ElCurrencyInput from "@/components/ElCurrencyInput.vue";
import PrincipalRevenueGuideDialog from "@/components/tutition_v3/dialog/PrincipalRevenueGuideDialog.vue";
import ManagingPrincipalRevenueService from "@/services/tutition_v3/ManagingPrincipalRevenueService";
import globalMixin from "@/global/mixin/global.mixin";
import NotifyMessage from "@/commonFuncion/NotifyMessage";
import {
  hasQuantityFraction,
  isQuantityPrecisionValid,
  toQuantityJsonNumber,
} from "@/components/tutition_v3/utils/quantity";
import {
  ATTEND_TYPE_IN_AFTER,
  ATTEND_TYPE_IN_BEFORE,
  ATTEND_TYPE_OUT_AFTER, CONFIG_KEY_IN_AFTER_LEARN, CONFIG_KEY_IN_BEFORE_EAT, CONFIG_KEY_IN_BEFORE_LEARN,
  CONFIG_KEY_OUT_AFTER_EAT,
  CONFIG_KEY_OUT_AFTER_LEARN,
  DEFAULT_UNITS,
  MONTH_LIST
} from "@/constant/tuitionConstant";


const defaultDataForm = () => ({
  // Nhóm 1 – Thông tin định danh
  name: "", category: "IN", description: "", unit: "",

  // Nhóm 2 – Tài chính
  price: 0, quantity: 1, issuingElectronicInvoices: false,

  // Nhóm 3 – Giảm giá
  discount: false, discountType: "PERCENT", discountNumber: 0,

  // Nhóm 4 – Điểm danh
  // attendanceType hợp lệ: LEARN | EAT | LATE | OTHER (theo API doc v2.0)
  attendance: false, calculationMethod: "BEFORE", attendanceType: null, configKey: "",
  attendanceVersion: "VERSION_ONESCHOOL",
  configValue: null,   // Giá trị tiền / định mức tổng thể (khi không dùng configList)
  configTime: null,    // Thời gian đón về, dùng khi attendanceType = LATE (HH:mm)
  configList: [],

  // Nhóm 5 – Thời gian áp dụng
  // appliedTimeType hợp lệ: YEAR | MONTH | TIME_PERIOD | UNIT (theo API doc v2.0)
  appliedTimeType: "MONTH",
  t1: false, t2: false, t3: false, t4: false,
  t5: false, t6: false, t7: false, t8: false,
  t9: false, t10: false, t11: false, t12: false,
  // defaultCycle: true, startDay: 1, endDay: 31,
  startDate: null, endDate: null, numberUnit: null,
});

export default {
  directives: { discountPercent: discountPercentInput },
  name: "PrincipalRevenueFormDialog",
  components: { DialogBase, ElCurrencyInput, PrincipalRevenueGuideDialog },
  mixins: [globalMixin],

  data() {
    const validateQuantity = (rule, value, callback) => {
      const v = Number(value);
      if (!isQuantityPrecisionValid(value, this.allowDecimal)) {
        callback(new Error(this.allowDecimal
          ? "Số lượng chỉ được có tối đa 2 chữ số thập phân"
          : "Số lượng phải là số nguyên"));
      } else if (this.dataForm.category === 'IN' && v <= 0) {
        callback(new Error("Khoản thu phải có số lượng > 0"));
      } else if (this.dataForm.category === 'OUT' && v >= 0) {
        callback(new Error("Khoản hoàn trả phải có số lượng < 0"));
      } else {
        callback();
      }
    };
    const validateDiscountType = (rule, value, callback) => {
      if (this.dataForm.discount && !value) callback(new Error("Vui lòng chọn loại giảm giá"));
      else callback();
    };
    const validateUnit = (rule, value, callback) => {
      // Unit phải tồn tại và không phải chuỗi trắng, giới hạn ký tự
      if (!this.dataForm.unit || (typeof this.dataForm.unit === 'string' && this.dataForm.unit.trim() === '')) {
        return callback(new Error('Vui lòng nhập đơn vị tính'));
      }
      if (typeof this.dataForm.unit === 'string' && this.dataForm.unit.length > 50) {
        return callback(new Error('Đơn vị tính không được vượt quá 50 ký tự'));
      }
      callback();
    };
    const validateDiscountNumber = (rule, value, callback) => {
      if (!this.dataForm.discount) return callback();
      if (value === null || value === undefined || value === "") return callback(new Error("Vui lòng nhập giá trị giảm"));
      if (this.dataForm.discountType === "PERCENT") {
        const v = Number(value) || 0;
        if (v < 0 || v > 100) return callback(new Error("Phần trăm giảm phải trong khoảng 0–100"));
      }
      callback();
    };
    const validateAttendanceType = (rule, value, callback) => {
      if (this.dataForm.attendance && !value) callback(new Error("Vui lòng chọn loại điểm danh"));
      else callback();
    };
    const validateCalculationMethod = (rule, value, callback) => {
      if (this.dataForm.attendance && !value) callback(new Error("Vui lòng chọn phương pháp tính"));
      else callback();
    };
    const validateConfigKey = (rule, value, callback) => {
      if (
        this.dataForm.attendance &&
        this.dataForm.attendanceType &&
        this.configKeyCurrentList.length > 0 &&
        !value
      ) {
        callback(new Error("Vui lòng chọn chi tiết điểm danh"));
      } else {
        callback();
      }
    };
    const validateStartDate = (rule, value, callback) => {
      if (this.dataForm.appliedTimeType === "TIME_PERIOD" && !value) callback(new Error("Vui lòng chọn ngày bắt đầu"));
      else callback();
    };
    const validateEndDate = (rule, value, callback) => {
      if (this.dataForm.appliedTimeType === "TIME_PERIOD" && !value) callback(new Error("Vui lòng chọn ngày kết thúc"));
      else callback();
    };
    const validateNumberUnit = (rule, value, callback) => {
      if (this.dataForm.appliedTimeType === "UNIT" && (!value || value < 1)) callback(new Error("Vui lòng nhập số đơn vị >= 1"));
      else callback();
    };

    return {
      MONTH_LIST,
      DEFAULT_UNITS,
      ATTEND_TYPE_IN_BEFORE,
      ATTEND_TYPE_IN_AFTER,
      ATTEND_TYPE_OUT_AFTER,
      CONFIG_KEY_IN_BEFORE_LEARN,
      CONFIG_KEY_IN_BEFORE_EAT,
      CONFIG_KEY_IN_AFTER_LEARN,
      CONFIG_KEY_OUT_AFTER_LEARN,
      CONFIG_KEY_OUT_AFTER_EAT,
      visible: false, loading: false, isEditMode: false, editingId: null, updateRegisteredOverride: false,
      allowDecimal: false,
      suppressAttendanceWatch: false,  // flag tắt watch khi đang load dữ liệu edit
      previewSumMoney: 0,
      discountAmount: 0,
      monthList: MONTH_LIST,
      unitsList: [...DEFAULT_UNITS],
      dataForm: defaultDataForm(),
      formRules: {
        name:           [{ required: true, message: "Vui lòng nhập tên khoản thu", trigger: "blur" }],
        category:       [{ required: true, message: "Vui lòng chọn loại khoản", trigger: "change" }],
        price:          [
          { required: true, message: "Vui lòng nhập đơn giá", trigger: "blur" },
          { type: "number", min: 0, message: "Đơn giá phải >= 0", trigger: "blur" },
        ],
        quantity:       [{ required: true, message: "Vui lòng nhập số lượng", trigger: "blur" }, { validator: validateQuantity, trigger: "blur" }],
        unit:             [{ validator: validateUnit, trigger: ["blur", "change"] }],
        discountType:        [{ validator: validateDiscountType,      trigger: "change" }],
        discountNumber:      [{ validator: validateDiscountNumber,    trigger: "blur" }],
        calculationMethod:   [{ validator: validateCalculationMethod, trigger: "change" }],
        attendanceType:      [{ validator: validateAttendanceType,    trigger: "change" }],
        configKey:           [{ validator: validateConfigKey,         trigger: "change" }],
        startDate:           [{ validator: validateStartDate,         trigger: "change" }],
        endDate:        [{ validator: validateEndDate,        trigger: "change" }],
        numberUnit:     [{ validator: validateNumberUnit,     trigger: "blur" }],
      },
    };
  },

  computed: {
    monthsAll: {
      get() {
        return this.monthList.every(m => !!this.dataForm[m.key]);
      },
      set(val) {
        this.monthList.forEach(m => { this.$set(this.dataForm, m.key, !!val); });
      },
    },

    // Danh sách loại điểm danh phụ thuộc category + calculationMethod
    attendanceTypeCurrentList() {
      const { category, calculationMethod } = this.dataForm;
      if (category === "IN") {
        return calculationMethod === "BEFORE" ? ATTEND_TYPE_IN_BEFORE : ATTEND_TYPE_IN_AFTER;
      }
      if (category === "OUT") {
        return ATTEND_TYPE_OUT_AFTER;
      }
      return [];
    },

    // Danh sách chi tiết (configKey) phụ thuộc category + calculationMethod + attendanceType
    configKeyCurrentList() {
      const { category, calculationMethod, attendanceType } = this.dataForm;
      if (category === "IN" && calculationMethod === "BEFORE") {
        if (attendanceType === "LEARN") return CONFIG_KEY_IN_BEFORE_LEARN;
        if (attendanceType === "EAT")     return CONFIG_KEY_IN_BEFORE_EAT;
      }
      if (category === "IN" && calculationMethod === "AFTER") {
        if (attendanceType === "LEARN") return CONFIG_KEY_IN_AFTER_LEARN;
        // LATE → không dùng configKey dropdown mà dùng configList block
      }
      if (category === "OUT") {
        if (attendanceType === "LEARN") return CONFIG_KEY_OUT_AFTER_LEARN;
        if (attendanceType === "EAT")     return CONFIG_KEY_OUT_AFTER_EAT;
      }
      return [];
    },

    // Computed để watch đồng thời các trường ảnh hưởng đến configKey
    attendanceContext() {
      return [this.dataForm.attendanceType, this.dataForm.calculationMethod, this.dataForm.category];
    },
  },

  watch: {
    "dataForm.price":             "calculatePreview",
    "dataForm.quantity":          "calculatePreview",
    "dataForm.discount":          "calculatePreview",
    "dataForm.discountType":      "calculatePreview",
    "dataForm.discountNumber":    "calculatePreview",
    // Reset configKey khi loại điểm danh, phương pháp tính hoặc category thay đổi
    // (bỏ qua lần đầu khi openForEdit đang load dữ liệu – configKey sẽ được gán qua $nextTick)
    attendanceContext(newVal, oldVal) {
      if (this.suppressAttendanceWatch) return;
      if (newVal[0] !== oldVal[0] || newVal[1] !== oldVal[1] || newVal[2] !== oldVal[2]) {
        this.dataForm.configKey = "";
      }
    },
    // Nếu user nhập một đơn vị mới (qua el-select allow-create) thì lưu tạm vào danh sách
    "dataForm.unit"(val) {
      if (!val) return;
      if (!this.unitsList.includes(val)) {
        this.unitsList.push(val);
      }
    },
  },

  methods: {
    // ─── API công khai ─────────────────────────────────────────────
    openForCreate() {
      this.isEditMode = false;
      this.editingId = null;
      this.updateRegisteredOverride = false;
      this.allowDecimal = false;
      this.dataForm = defaultDataForm();
      this.previewSumMoney = 0;
      this.discountAmount = 0;
      this.visible = true;
      this.$nextTick(() => this.$refs.dataForm?.clearValidate());
    },

    async openForEdit(id) {
      this.isEditMode = true;
      this.editingId = id;
      this.updateRegisteredOverride = false;
      this.allowDecimal = false;
      this.dataForm = defaultDataForm();
      this.previewSumMoney = 0;
      this.discountAmount = 0;
      this.visible = true;

      try {
        const { data } = await ManagingPrincipalRevenueService.findById(id);
        const d = data.data;

        // Tắt watch attendanceContext khi đang load để tránh reset configKey
        this.suppressAttendanceWatch = true;

        let inferredAttendanceType = d.attendanceType || this._getInferredAttendanceType(d);
        if (inferredAttendanceType === "PICKUP_LATE") {
          inferredAttendanceType = "LATE";
        }

        this.dataForm = {
          name:                      d.name                      || "",
          category:                  d.category                  || "IN",
          description:               d.description               || "",
          unit:                      d.unit                      || "",
          price:                     d.price                     ?? 0,
          quantity:                  d.quantity === undefined ? 1 : d.quantity,
          issuingElectronicInvoices: d.issuingElectronicInvoices ?? false,
          discount:                  d.discount                  ?? false,
          discountType:              d.discountType              || "PERCENT",
          discountNumber:            d.discountNumber            ?? 0,
          attendance:                d.attendance                ?? false,
          calculationMethod:         d.calculationMethod         || "BEFORE",
          attendanceType:            inferredAttendanceType,
          configKey:                 d.configKey                 || "",
          attendanceVersion:         d.attendanceVersion         || "VERSION1",
          configValue:               d.configValue               ?? null,
          configTime:                d.configTime                || null,
          configList:                Array.isArray(d.configList) ? d.configList.map(c => ({
            configName:      c.configName      || "",
            configStartTime: c.configStartTime || "",
            configEndTime:   c.configEndTime   || "",
            configValue:     c.configValue     ?? 0,
          })) : [],
          appliedTimeType:           d.appliedTimeType           || null,
          t1:  d.t1  ?? false, t2:  d.t2  ?? false, t3:  d.t3  ?? false,
          t4:  d.t4  ?? false, t5:  d.t5  ?? false, t6:  d.t6  ?? false,
          t7:  d.t7  ?? false, t8:  d.t8  ?? false, t9:  d.t9  ?? false,
          t10: d.t10 ?? false, t11: d.t11 ?? false, t12: d.t12 ?? false,
          // defaultCycle: d.defaultCycle ?? true,
          // startDay:     d.startDay     ?? 1,
          // endDay:       d.endDay       ?? 31,
          startDate:    d.startDate    || null,
          endDate:      d.endDate      || null,
          numberUnit:   d.numberUnit   || null,
        };
        this.allowDecimal = hasQuantityFraction(this.dataForm.quantity);
        this.$nextTick(() => {
          this.suppressAttendanceWatch = false;
          this.calculatePreview();
        });
      } catch (err) {
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Không thể tải thông tin khoản thu!"
        );
        this.visible = false;
        return;
      }
      this.$nextTick(() => this.$nextTick(() => this.$refs.dataForm?.clearValidate()));
    },

    // ─── Submit ────────────────────────────────────────────────────
    async handleSubmit() {
      try { await this.$refs.dataForm.validate(); } catch { return; }

      // Confirm dialog khi updateRegisteredOverride = true
      if (this.isEditMode && this.updateRegisteredOverride) {
        try {
          await this.$confirm(
            "Bạn đang chọn áp dụng giá mới cho TẤT CẢ học sinh đã đăng ký khoản tổng sử dụng khoản phụ này.\n\n" +
            "Giá trị riêng đã sửa cho từng HS trước đó sẽ BỊ MẤT và thay bằng giá mới.\n\n" +
            "Hành động này không thể hoàn tác.",
            "Xác nhận áp dụng thay đổi",
            {
              confirmButtonText: "Xác nhận áp dụng",
              cancelButtonText: "Hủy",
              type: "warning",
            }
          );
        } catch {
          // User hủy → không submit
          return;
        }
      }

      this.loading = true;
      try {
        const payload = this._buildPayload();
        if (hasOverLimitPercentDiscount(payload)) {
          NotifyMessage.leftCornerNotificationError(DISCOUNT_PERCENT_LIMIT_MESSAGE);
          return;
        }

        if (this.isEditMode) {
          const res = await ManagingPrincipalRevenueService.update(this.editingId, payload);
          NotifyMessage.leftCornerNotificationSuccess(res.data.message || "Cập nhật khoản thu thành công!");
        } else {
          const res = await ManagingPrincipalRevenueService.create(payload);
          NotifyMessage.leftCornerNotificationSuccess(res.data.message || "Thêm mới khoản thu thành công!");
        }
        this.visible = false;
        this.$emit("saved");
      } catch (err) {
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Lưu khoản thu thất bại!"
        );
      } finally {
        this.loading = false;
      }
    },

    handleClose() { this.visible = false; },

    openGuideDialog() {
      this.$refs.guideDialog?.open();
    },

    handleAllowDecimalChange(value) {
      if (!value && hasQuantityFraction(this.dataForm.quantity)) {
        NotifyMessage.leftCornerNotificationWarning(
          "Vui lòng sửa số lượng về số nguyên trước khi tắt chế độ thập phân."
        );
        return;
      }
      this.allowDecimal = value;
      this.$nextTick(() => this.$refs.dataForm?.validateField("quantity"));
    },

    // ─── Build payload theo API docs ───────────────────────────────
    _buildPayload() {
      const f = this.dataForm;
      // ⚠️ KHÔNG gửi: idSchool, idCreated, createdBy, idModified, lastModifiedBy
      // Backend tự lấy từ JWT token (theo API doc v2.0)
      const payload = {
        // Nhóm 1 – Định danh & phân loại (BẮT BUỘC)
        name:                      f.name,
        category:                  f.category,
        price:                     f.price,

        // Nhóm 2 – Tài chính bổ sung
        description:               f.description || null,
        quantity:                  toQuantityJsonNumber(f.quantity),
        unit:                      f.unit || null,
        issuingElectronicInvoices: f.issuingElectronicInvoices,

        // Nhóm 3 – Giảm giá
        discount:                  f.discount,
        discountType:              f.discount ? f.discountType  : null,
        discountNumber:            f.discount ? f.discountNumber : null,

        // Nhóm 4 – Điểm danh
        attendance:                f.attendance,
        attendanceType:            f.attendance ? f.attendanceType : null,
        attendanceVersion:         f.attendance ? (f.attendanceVersion || "VERSION1") : null,

        // Nhóm 6 – Chi tiết điểm danh
        calculationMethod:         f.attendance ? (f.calculationMethod || "BEFORE") : null,
        configKey:                 f.attendance
          ? (f.attendanceType === "LATE"
              ? "LATE"
              : (this.configKeyCurrentList.length > 0 ? (f.configKey || null) : null))
          : null,
        configValue:               f.attendance ? (f.configValue ?? null) : null,
        configTime:                f.attendance && f.attendanceType === "LATE" ? (f.configTime || null) : null,
        configList:                (f.attendance && f.attendanceType === "LATE" && Array.isArray(f.configList)) ? f.configList : [],

        // Nhóm 5 – Thời gian áp dụng
        appliedTimeType:           f.appliedTimeType || null,
      };

      if (f.appliedTimeType === "YEAR") {
        // YEAR: không cần trường bổ sung
        MONTH_LIST.forEach(m => { payload[m.key] = false; });
        // payload.defaultCycle = null;
        // payload.startDay     = null;
        // payload.endDay       = null;
        payload.startDate    = null;
        payload.endDate      = null;
      } else if (f.appliedTimeType === "MONTH") {
        MONTH_LIST.forEach(m => { payload[m.key] = f[m.key]; });
        // payload.defaultCycle = f.defaultCycle;
        // payload.startDay     = f.defaultCycle ? null : f.startDay;
        // payload.endDay       = f.defaultCycle ? null : f.endDay;
        payload.startDate    = null;
        payload.endDate      = null;
      } else if (f.appliedTimeType === "TIME_PERIOD") {
        MONTH_LIST.forEach(m => { payload[m.key] = false; });
        // payload.defaultCycle = null;
        // payload.startDay     = null;
        // payload.endDay       = null;
        payload.startDate    = f.startDate;
        payload.endDate      = f.endDate;
      } else if (f.appliedTimeType === "UNIT") {
        MONTH_LIST.forEach(m => { payload[m.key] = false; });
        // payload.defaultCycle = null;
        // payload.startDay     = null;
        // payload.endDay       = null;
        payload.startDate    = null;
        payload.endDate      = null;
      } else {
        MONTH_LIST.forEach(m => { payload[m.key] = false; });
        // payload.defaultCycle = null;
        // payload.startDay     = null;
        // payload.endDay       = null;
        payload.startDate    = null;
        payload.endDate      = null;
      }

      // numberUnit luôn gửi đi, không phụ thuộc appliedTimeType
      payload.numberUnit = f.numberUnit;

      // Gửi updateRegisteredOverride khi đang sửa (edit mode)
      if (this.isEditMode) {
        payload.updateRegisteredOverride = this.updateRegisteredOverride;
      }

      return payload;
    },

    // ─── Tính tiền tại FE (preview) ────────────────────────────────
    calculatePreview() {
      const { price, quantity, discount, discountType, discountNumber } = this.dataForm;
      const base = (Number(price) || 0) * (Number(quantity) || 0);
      if (!discount) {
        this.discountAmount  = 0;
        this.previewSumMoney = base;
        return;
      }
      if (discountType === "PERCENT") {
        const pct = Number(discountNumber) || 0;
        this.discountAmount = Math.round(base * pct / 100);
      } else {
        this.discountAmount = Number(discountNumber) || 0;
      }
      if (this.discountAmount > base) {
        this.$alert("Số tiền giảm không thể lớn hơn tổng tiền!", "Thông báo", {
          confirmButtonText: "Đã hiểu",
          callback: () => {
            this.dataForm.discountNumber = 0;
            this.discountAmount = 0;
            this.calculatePreview();
          },
        });
        this.previewSumMoney = base;
      } else {
        this.previewSumMoney = base - this.discountAmount;
      }
    },

    // ─── Sự kiện field ────────────────────────────────────────────
    onDiscountChange(val)       { if (!val) { this.dataForm.discountType = "PERCENT"; this.dataForm.discountNumber = 0; } this.calculatePreview(); },
    onAttendanceChange(val)     { if (!val) { this.dataForm.calculationMethod = "BEFORE"; this.dataForm.attendanceType = null; this.dataForm.configKey = ""; this.dataForm.configValue = null; this.dataForm.configTime = null; this.dataForm.configList = []; } },
    // Khi đổi loại khoản → ép lại calculationMethod nếu là OUT, reset type + configKey
    onCategoryChange(val) {
      if (val === "OUT") {
        this.dataForm.calculationMethod = "AFTER";
      }
      this.dataForm.attendanceType = null;
      this.dataForm.configKey = "";
      this.dataForm.configValue = null;
      this.dataForm.configTime = null;
      this.dataForm.configList = [];
    },
    // Reset loại điểm danh và configKey khi đổi phương pháp tính
    resetAttendanceMethod()     { this.dataForm.attendanceType = null; this.dataForm.configKey = ""; this.dataForm.configValue = null; this.dataForm.configTime = null; },
    // Reset configKey khi đổi loại điểm danh; tự thêm 1 block mặc định nếu chọn LATE
    changeAttendanceTypeMethod(val) {
      this.dataForm.configKey = "";
      this.dataForm.configValue = null;
      this.dataForm.configTime = null;
      this.dataForm.configList = [];
      if (val === "LATE") {
        this.dataForm.configList.push({ configName: "", configStartTime: "", configEndTime: "", configValue: 0 });
      }
    },
    onAppliedTimeTypeChange()   {
      this.dataForm.t1  = false; this.dataForm.t2  = false; this.dataForm.t3  = false;
      this.dataForm.t4  = false; this.dataForm.t5  = false; this.dataForm.t6  = false;
      this.dataForm.t7  = false; this.dataForm.t8  = false; this.dataForm.t9  = false;
      this.dataForm.t10 = false; this.dataForm.t11 = false; this.dataForm.t12 = false;
      // this.dataForm.defaultCycle = true;
      // this.dataForm.startDay = 1; this.dataForm.endDay = 31;
      this.dataForm.startDate = null; this.dataForm.endDate = null;
    },

    // ─── configList helpers ────────────────────────────────────────
    addConfig() {
      this.dataForm.configList.push({ configName: "", configStartTime: "", configEndTime: "", configValue: 0 });
    },
    removeConfig(idx) {
      this.dataForm.configList.splice(idx, 1);
    },

    formatMoney(value) {
      if (value === null || value === undefined || value === "") return "—";
      return Number(value).toLocaleString("vi-VN") + " ₫";
    },

    _getInferredAttendanceType(d) {
      if (d.attendanceType) return d.attendanceType;
      if (!d.attendance) return null;

      const configKey = d.configKey;
      if (configKey) {
        if (CONFIG_KEY_IN_BEFORE_LEARN.some(item => item.key === configKey) ||
            CONFIG_KEY_IN_AFTER_LEARN.some(item => item.key === configKey) ||
            CONFIG_KEY_OUT_AFTER_LEARN.some(item => item.key === configKey)) {
          return "LEARN";
        }
        if (CONFIG_KEY_IN_BEFORE_EAT.some(item => item.key === configKey) ||
            CONFIG_KEY_OUT_AFTER_EAT.some(item => item.key === configKey)) {
          return "EAT";
        }
      } else if (d.configList && d.configList.length > 0) {
        return "LATE";
      }
      return null;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/style/sk-dialog";

/deep/ .el-input-number.sk-u-w-full { width: 100%; }
/deep/ .el-date-editor.sk-u-w-full  { width: 100%; }

/* Ô Thành tiền (read-only, FE preview) */
.prf-preview-money {
  padding: 10px 12px;
  width: 100%;
  text-align: right;
  background: #f0f4f8;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  font-weight: 700;
  font-size: 15px;
  color: #1d4ed8;
  font-variant-numeric: tabular-nums;
  box-sizing: border-box;
}

/* Icon help */
.prf-help-icon {
  color: #64748b;
  font-size: 14px;
  vertical-align: middle;
}

.prf-help-entry {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
}

/* Discount amount display */
.prf-discount-amount {
  color: #dc2626;
  font-weight: 700;
  min-width: 120px;
  text-align: right;
}

/* Month checkbox grid */
.prf-month-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.prf-month-cb {
  margin-left: 0 !important;
  margin-right: 0 !important;
}

/* Config row */
.prf-config-row {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 8px 10px;
}

/* Config header labels */
.prf-config-header {
  padding: 0 10px;
}
.prf-col-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
}

/* Empty config hint */
.prf-empty-config {
  color: #94a3b8;
  font-style: italic;
  font-size: 13px;
  margin-bottom: 8px;
}

/* Override checkbox & warning */
.prf-override-checkbox {
  font-weight: 600;
}
.prf-override-warning {
  margin-top: 8px;
  padding: 10px 14px;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  font-size: 13px;
  color: #92400e;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  line-height: 1.5;

  i {
    font-size: 16px;
    color: #d97706;
    margin-top: 1px;
    flex-shrink: 0;
  }
}
</style>
